import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Circle,
} from "react-leaflet";
import { Button, Col, Row } from "antd";
import "./ProductMap.scss";
import Moisture from "@assets/icons/Moisture.svg";
import Rain from "@assets/icons/Rain.svg";
import TotalWeight from "@assets/icons/Total-weight.svg";
import hayCut from "@assets/icons/Hay-cut.svg";
import MapFilter from "@components/MapFilter/MapFilter";
import "leaflet/dist/leaflet.css";
import Helpers from "@utils/Helpers";
import L from "leaflet";
import cheapFew from "@assets/icons/CheapFew.svg";
import cheapAverage from "@assets/icons/CheapAverage.svg";
import cheapAlot from "@assets/icons/CheapAlot.svg";
import averageFew from "@assets/icons/AverageFew.svg";
import averageAverage from "@assets/icons/AverageAverage.svg";
import averageAlot from "@assets/icons/AverageAlot.svg";
import expensiveFew from "@assets/icons/ExpensiveFew.svg";
import expensiveAverage from "@assets/icons/ExpensiveAverage.svg";
import expensiveAlot from "@assets/icons/ExpensiveAlot.svg";
import { useUser } from "contexts/UserContext";
import homeIcon from "@assets/icons/homeIcon.png";
import location from "@assets/icons/location.png";
import { errorLog } from "logging";
import mapApi from "services/MapApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import distanceIcon from "@assets/icons/distanceIcon.png";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { TruckOutlined } from "@ant-design/icons";
import { useUnit } from "contexts/UnitContext";

const center: [number, number] = [55.0, -115.0];

interface IProductMap {
  productNearBy?: any;
  mainId?: number;
  setDistance?: (distance: any) => void;
  setDeliveryTime?: (time: any) => void;
  isProductDetail: boolean;
  setUrlParams?: (urlParams: any) => void;
  searchParam?: any;
}

const ProductMap: React.FC<IProductMap> = ({
  productNearBy,
  mainId,
  setDistance,
  setDeliveryTime,
  isProductDetail,
  searchParam,
  setUrlParams,
}) => {
  const { userProfile } = useUser();
  const { units, convertDistance, convertCurrency } = useUnit();
  const [circleCenter, setCircleCenter] = useState<[number, number]>(center);
  const [originalLocations, setOriginalLocations] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedPriceFilter, setSelectedPriceFilter] = useState<number[]>([]);
  const [selectedQuantityFilter, setSelectedQuantityFilter] = useState<
    number[]
  >([]);
  const [mapDistance, setMapDistance] = useState(0);
  const [mapTime, setMapTime] = useState(0);
  const [isSetupDone, setIsSetupDone] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const [routeCoordinates, setRouteCoordinates] = useState<[number, number][]>(
    []
  );
  const [isOpenRoutingInfo, setIsOpenRoutingInfo] = useState(false);

  useEffect(() => {
    if (productNearBy && Array.isArray(productNearBy)) {
      const newLocations: any[] = [];
      productNearBy.forEach((product: any) => {
        const classify = classifyProduct(product);

        const items = {
          product_id: product.product_id,
          name: product.hay_type,
          position: [product.lat, product.lon],
          hay_cut: product.hay_cut,
          quantity: product.quantity,
          rain: product.rain,
          moisture: product.moisture,
          image: product.image,
          total_weight: product.total_weight,
          price_per_tons: product.price_per_tons,
          price_per_bale: product.price_per_bale,
          unit: product.unit,
          hay_type: product.hay_type,
          priceQuality: classify.priceQuality,
          quantityQuality: classify.quantityQuality,
          icon: L.divIcon({
            className: "custom-icon-class",
            html: `<div class="icon-container">
              <img src="${classify.icon}" class="icon ${
              product.product_id === mainId ? "main-icon" : ""
            }" alt="icon" />
           </div>`,
            iconSize: [30, 45],
            iconAnchor: [15, 15],
            popupAnchor: [0, -45],
          }),
        };
        newLocations.push(items);
      });
      if (userProfile) {
        const home = {
          product_id: 1,
          position: [userProfile.lat, userProfile.lon],
          icon: L.divIcon({
            className: "custom-icon-class z-index-1",
            html: `<div class="icon-container">
              <img src="${homeIcon}" class="icon" alt="icon" />
           </div>`,
            iconSize: [30, 45],
            iconAnchor: [15, 15],
            popupAnchor: [0, -45],
          }),
        };
        newLocations.push(home);
        if (isProductDetail) {
          const filteredLocation = newLocations.filter(
            (item: any) => item.product_id === mainId
          );

          mapRouting(
            [userProfile.lat, userProfile.lon],
            filteredLocation[0].position
          );
        }
      }
      setLocations(newLocations);
      setOriginalLocations(newLocations);
    }
  }, [productNearBy]);

  const mapRouting = async (homeLocations: any[], targetLocation: any[]) => {
    try {
      const response = await mapApi.mapRouting(targetLocation, homeLocations);
      if (response.status === 200) {
        const responseData = response.data;
        const coordinates =
          responseData.features[0].geometry.coordinates[0].map(
            (coord: [number, number]) => [coord[1], coord[0]]
          );

        let distance = responseData.features[0].properties.distance;
        const time = responseData.features[0].properties.time;
        setMapDistance(distance);
        setMapTime(time);
        setIsOpenRoutingInfo(true);
        setDistance?.(distance);
        setDeliveryTime?.(time);
        setRouteCoordinates(coordinates);
      }
    } catch (error: any) {
      if (error.status === 400) {
        Helpers.notification.warning(error.response.data.message);
      } else {
        Helpers.notification.error(
          "Can't calculate distance between product and your location"
        );
      }
      errorLog("ProductMap.tsx - mapRouting", error);
    }
  };

  const classifyProduct = (product: any) => {
    let priceQuality;
    if (product.price_per_tons >= 450) {
      priceQuality = 3;
    }
    if (product.price_per_tons <= 300) {
      priceQuality = 1;
    }
    if (product.price_per_tons > 300 && product.price_per_tons < 450) {
      priceQuality = 2;
    }

    let quantityQuality;
    if (product.quantity >= 30) {
      quantityQuality = 3;
    }
    if (product.quantity <= 10) {
      quantityQuality = 1;
    }
    if (product.quantity > 10 && product.quantity < 30) {
      quantityQuality = 2;
    }

    let icon;
    if (priceQuality === 1 && quantityQuality === 1) {
      icon = cheapFew;
    }
    if (priceQuality === 1 && quantityQuality === 2) {
      icon = cheapAverage;
    }
    if (priceQuality === 1 && quantityQuality === 3) {
      icon = cheapAlot;
    }
    if (priceQuality === 2 && quantityQuality === 1) {
      icon = averageFew;
    }
    if (priceQuality === 2 && quantityQuality === 2) {
      icon = averageAverage;
    }
    if (priceQuality === 2 && quantityQuality === 3) {
      icon = averageAlot;
    }
    if (priceQuality === 3 && quantityQuality === 1) {
      icon = expensiveFew;
    }
    if (priceQuality === 3 && quantityQuality === 2) {
      icon = expensiveAverage;
    }
    if (priceQuality === 3 && quantityQuality === 3) {
      icon = expensiveAlot;
    }

    return { priceQuality, quantityQuality, icon };
  };

  useEffect(() => {
    let summarizeFilter = [...originalLocations];
    if (selectedPriceFilter.length > 0 || selectedQuantityFilter.length > 0) {
      if (selectedPriceFilter.length > 0) {
        summarizeFilter = summarizeFilter.filter((item: any) =>
          selectedPriceFilter.includes(item.priceQuality)
        );
      }

      if (selectedQuantityFilter.length > 0) {
        summarizeFilter = summarizeFilter.filter((item: any) =>
          selectedQuantityFilter.includes(item.quantityQuality)
        );
      }
      if (userProfile) {
        summarizeFilter.push(originalLocations[originalLocations.length - 1]);
      }

      setLocations(summarizeFilter);
    } else {
      if (originalLocations.length > 0) {
        setLocations([...originalLocations]);
      }
    }
  }, [selectedPriceFilter, selectedQuantityFilter]);

  const calculateBounds = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): any[] => {
    const southWest = [Math.min(lat1, lat2), Math.min(lon1, lon2)];
    const northEast = [Math.max(lat1, lat2), Math.max(lon1, lon2)];
    return [southWest, northEast];
  };

  useEffect(() => {
    if (locations.length > 0 && mapRef.current && !isSetupDone) {
      const map = mapRef.current;
      if (isProductDetail) {
        const filteredLocation = locations.filter(
          (item: any) => item.product_id === mainId
        );

        const [lat1, lon1] = filteredLocation[0].position;
        if (userProfile) {
          const [lat2, lon2] = locations[locations.length - 1].position;
          const bounds = calculateBounds(lat1, lon1, lat2, lon2);
          mapRef.current.fitBounds(bounds, {
            padding: [50, 50],
          });
        } else {
          map.setView([lat1, lon1], 5);
        }
        setIsSetupDone(true);
      } else {
        if (userProfile && userProfile.lat && userProfile.lon) {
          map.setView([userProfile?.lat!, userProfile?.lon!], 5);
        } else {
          map.setView(center, 5);
        }
      }
    }
  }, [locations]);

  const handleChangeRouting = (targetLocation: any) => {
    const homeLocation = [userProfile?.lat, userProfile?.lon];
    mapRouting(homeLocation, targetLocation.position);
  };

  const createLabelIcon = (label: string) => {
    return L.divIcon({
      html: `<div class="circle-label text-muted">${label}</div>`,
      className: "",
      iconSize: [0, 0],
      iconAnchor: [15, 10],
    });
  };

  const getPointOnCircle = (
    circleCenter: any,
    radiusInMeters: any,
    bearingInDegrees: any
  ): any => {
    const R = 6378137;
    const bearing = (bearingInDegrees * Math.PI) / 180;

    const lat1 = (circleCenter[0] * Math.PI) / 180;
    const lon1 = (circleCenter[1] * Math.PI) / 180;

    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(radiusInMeters / R) +
        Math.cos(lat1) * Math.sin(radiusInMeters / R) * Math.cos(bearing)
    );
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(radiusInMeters / R) * Math.cos(lat1),
        Math.cos(radiusInMeters / R) - Math.sin(lat1) * Math.sin(lat2)
      );

    return [(lat2 * 180) / Math.PI, (lon2 * 180) / Math.PI];
  };

  useEffect(() => {
    if (userProfile && userProfile.lat && userProfile.lon) {
      setCircleCenter([userProfile.lat, userProfile.lon]);
    } else {
      if (locations.length > 0) {
        setCircleCenter(locations[0].position);
      }
    }
  }, [userProfile, locations]);

  const handleViewProduct = (productId: any) => {
    if (!isProductDetail) {
      Helpers.redirect(`/product/detail?productId=${productId}`);
    } else {
      setUrlParams?.({ productId: productId });
    }
  };
  return (
    <div className="ProductMap">
      <MapFilter
        selectedPriceFilter={selectedPriceFilter}
        setSelectedPriceFilter={setSelectedPriceFilter}
        selectedQuantityFilter={selectedQuantityFilter}
        setSelectedQuantityFilter={setSelectedQuantityFilter}
      />
      <div className={`RoutingInfo ${isOpenRoutingInfo ? "display" : ""}`}>
        <div className="info-wrap">
          <div className="info-item">
            <TruckOutlined />
          </div>{" "}
          |
          <div className="info-item">
            <img src={distanceIcon} width={15} alt="dist" />{" "}
            <span>
              {Helpers.formatNumber(convertDistance(mapDistance / 1000), 0)}{" "}
              {units.distance}
            </span>
          </div>
          <div className="info-item">
            <FontAwesomeIcon icon={faClock} />{" "}
            <span>{Helpers.convertSecondsToHoursAndMinutes(mapTime)}</span>
          </div>
        </div>
      </div>
      <MapContainer
        center={center}
        zoom={5}
        style={{ height: "575px", width: "100%", borderRadius: "24px" }}
        ref={(mapInstance) => {
          if (mapInstance && !mapRef.current) {
            mapRef.current = mapInstance;
          }
        }}
      >
        <Circle
          center={circleCenter}
          radius={100000}
          color="white"
          fillColor="white"
          weight={0.5}
          fillOpacity={0.2}
        />
        <Marker
          position={getPointOnCircle(circleCenter, 100000, 0)}
          icon={createLabelIcon("100km")}
        />
        <Circle
          center={circleCenter}
          radius={300000}
          color="white"
          fillColor="white"
          weight={0.5}
          fillOpacity={0.2}
        />
        <Marker
          position={getPointOnCircle(circleCenter, 300000, 0)}
          icon={createLabelIcon("300km")}
        />
        <Circle
          center={circleCenter}
          radius={500000}
          color="white"
          fillColor="white"
          weight={0.5}
          fillOpacity={0.2}
        />
        <Marker
          position={getPointOnCircle(circleCenter, 500000, 0)}
          icon={createLabelIcon("500km")}
        />

        {searchParam && searchParam.location && searchParam.radius && (
          <>
            <Circle
              center={searchParam.location}
              radius={searchParam.radius * 1000}
              color="red"
              fillColor="red"
              weight={0.3}
              fillOpacity={0.1}
            />
            <Marker
              position={searchParam.location}
              icon={L.divIcon({
                className: "custom-icon-class z-index-1",
                html: `<div class="icon-container">
              <img src="${location}" class="location-icon" />
           </div>`,
                iconSize: [30, 45],
                iconAnchor: [15, 20],
                popupAnchor: [0, -45],
              })}
            />
          </>
        )}

        <TileLayer
          url={`https://maps.geoapify.com/v1/tile/klokantech-basic/{z}/{x}/{y}.png?apiKey=${process.env.REACT_APP_GEOAPIFY_KEY}`}
        />
        {locations.map((locationItem: any) => {
          return (
            <Marker
              key={locationItem.product_id}
              position={locationItem.position}
              icon={locationItem.icon}
            >
              {locationItem.product_id !== 1 && (
                <Popup className="popup">
                  <Row gutter={16}>
                    <Col span={10}>
                      <img
                        className="img-product-similar"
                        src={locationItem.image}
                        alt="product"
                      />
                    </Col>
                    <Col span={14}>
                      <div className="product-info mb-2">
                        <span>
                          Price per{" "}
                          {locationItem.unit == "Ton" ? "ton" : "bale"}:{" "}
                        </span>
                        <br />$
                        {Helpers.formatNumber(
                          convertCurrency(
                            parseFloat(
                              locationItem?.unit == "Ton"
                                ? locationItem?.price_per_tons
                                : locationItem?.price_per_bale
                            ),
                            "USD",
                            units.currency
                          ),
                          2
                        )}
                        /{locationItem?.unit}
                      </div>
                      <div className="product-info">
                        <span>Quantity: </span>
                        {locationItem.quantity} bales
                      </div>
                      <div className="product-info">
                        <img className="me-2" src={TotalWeight} alt="total" />
                        {Helpers.formatNumber(
                          locationItem?.total_weight / 1000,
                          2
                        )}{" "}
                        ton
                      </div>
                      <div className="product-info">
                        <img className="me-2" src={hayCut} alt="haycut" />
                        {locationItem.hay_cut}
                      </div>
                      <div className="product-info">
                        <img className="me-2" src={Moisture} alt="moist" />
                        {locationItem.moisture}%
                      </div>
                      <div className="product-info">
                        <img className="me-2" src={Rain} alt="rain" />
                        {locationItem.rain}
                      </div>
                      <div className="product-action">
                        <Button
                          className="view-btn"
                          size="small"
                          onClick={() =>
                            handleViewProduct(locationItem.product_id)
                          }
                        >
                          View
                        </Button>
                        {!isProductDetail && (
                          <Button
                            className="routing-btn"
                            onClick={() => handleChangeRouting(locationItem)}
                          >
                            Routing
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Popup>
              )}
            </Marker>
          );
        })}
        {routeCoordinates.length > 0 && (
          <Polyline positions={routeCoordinates} color="#f9a72e" />
        )}
      </MapContainer>
    </div>
  );
};

export default ProductMap;
